/**
 * MatchDataService
 * Pure functions for fetching, filtering, and grouping match data
 */

export interface Match {
  wedstrijdcode: string;
  thuisteam: string;
  uitteam: string;
  wedstrijddatum: string;
  aanvangstijd: string;
  veld: string;
  kleedkamerthuisteam: string;
  kleedkameruitteam: string;
  thuisteamlogo: string;
  uitteamlogo: string;
}

export interface MatchGroup {
  date: string;
  dateLabel: string;
  matches: Match[];
}

/**
 * Parse a date string to a Date object (date only, no time)
 * Supports ISO format (2026-03-21T09:00:00+0100) and DD-MM-YYYY format
 */
export function parseDate(dateString: string): Date | null {
  if (!dateString) return null;

  // Try ISO format first (YYYY-MM-DDTHH:mm:ss)
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateString);
  if (isoMatch) {
    const year = Number.parseInt(isoMatch[1], 10);
    const month = Number.parseInt(isoMatch[2], 10);
    const day = Number.parseInt(isoMatch[3], 10);
    return new Date(year, month - 1, day);
  }

  // Fall back to DD-MM-YYYY format
  const ddmmMatch = /^(\d{2})-(\d{2})-(\d{4})$/.exec(dateString);
  if (!ddmmMatch) return null;

  const day = Number.parseInt(ddmmMatch[1], 10);
  const month = Number.parseInt(ddmmMatch[2], 10);
  const year = Number.parseInt(ddmmMatch[3], 10);

  if (month < 1 || month > 12 || day < 1 || day > 31) return null;

  return new Date(year, month - 1, day);
}

/**
 * Extract just the date portion (YYYY-MM-DD) from a datetime string for grouping
 */
export function extractDateKey(dateString: string): string {
  if (!dateString) return '';

  // For ISO format, extract YYYY-MM-DD
  const isoMatch = /^(\d{4}-\d{2}-\d{2})/.exec(dateString);
  if (isoMatch) {
    return isoMatch[1];
  }

  // For DD-MM-YYYY, convert to YYYY-MM-DD for consistent sorting
  const ddmmMatch = /^(\d{2})-(\d{2})-(\d{4})$/.exec(dateString);
  if (ddmmMatch) {
    return `${ddmmMatch[3]}-${ddmmMatch[2]}-${ddmmMatch[1]}`;
  }

  return dateString;
}

/**
 * Format a Date object to a Dutch date label (e.g., "Zaterdag 22 maart")
 */
export function formatDateLabel(date: Date): string {
  const days = ['Zondag', 'Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag'];
  const months = [
    'januari',
    'februari',
    'maart',
    'april',
    'mei',
    'juni',
    'juli',
    'augustus',
    'september',
    'oktober',
    'november',
    'december',
  ];

  const dayName = days[date.getDay()];
  const dayNum = date.getDate();
  const monthName = months[date.getMonth()];

  return `${dayName} ${dayNum} ${monthName}`;
}

/**
 * Group matches by their date
 * Returns an array of MatchGroup objects sorted by date
 */
export function groupByDate(matches: Match[]): MatchGroup[] {
  if (!matches || matches.length === 0) {
    return [];
  }

  const groups = new Map<string, Match[]>();

  for (const match of matches) {
    const dateKey = extractDateKey(match.wedstrijddatum);
    if (!groups.has(dateKey)) {
      groups.set(dateKey, []);
    }
    groups.get(dateKey)!.push(match);
  }

  const result: MatchGroup[] = [];

  for (const [dateKey, dateMatches] of groups) {
    const parsedDate = parseDate(dateKey);
    const dateLabel = parsedDate ? formatDateLabel(parsedDate) : dateKey;

    result.push({
      date: dateKey,
      dateLabel,
      matches: dateMatches,
    });
  }

  // Sort by date
  result.sort((a, b) => {
    const dateA = parseDate(a.date);
    const dateB = parseDate(b.date);
    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;
    return dateA.getTime() - dateB.getTime();
  });

  return result;
}

/**
 * Filter matches to only include those within the specified number of days from now
 */
export function filterByDaysAhead(
  matches: Match[],
  daysAhead: number,
  referenceDate: Date = new Date()
): Match[] {
  if (!matches || matches.length === 0) {
    return [];
  }

  // Start of reference day (midnight)
  const startOfDay = new Date(referenceDate);
  startOfDay.setHours(0, 0, 0, 0);

  // End of the range (end of the last day)
  const endOfRange = new Date(startOfDay);
  endOfRange.setDate(endOfRange.getDate() + daysAhead);
  endOfRange.setHours(23, 59, 59, 999);

  return matches.filter((match) => {
    const matchDate = parseDate(match.wedstrijddatum);
    if (!matchDate) return false;

    return matchDate >= startOfDay && matchDate <= endOfRange;
  });
}

/**
 * Build the Sportlink API URL with the given parameters
 */
export function buildApiUrl(clientId: string, daysAhead: number): string {
  const fields = [
    'wedstrijdcode',
    'thuisteam',
    'uitteam',
    'wedstrijddatum',
    'aanvangstijd',
    'veld',
    'kleedkamerthuisteam',
    'kleedkameruitteam',
    'thuisteamlogo',
    'uitteamlogo',
  ];

  const params = new URLSearchParams({
    client_id: clientId,
    gebruiklokaleteamgegevens: 'NEE',
    aantaldagen: String(daysAhead),
    aantalregels: '100',
    weekoffset: '0',
    eigenwedstrijden: 'JA',
    thuis: 'JA',
    uit: 'NEE',
    sorteervolgorde: 'datum',
    spelsoort: '',
    leeftijdscategorie: 'alles',
  });

  fields.forEach((field) => params.append('fields[]', field));

  return `https://data.sportlink.com/programma?${params.toString()}`;
}

/**
 * Extract initials from a team name for fallback badge display
 * - Takes first letter of first two "words" if multiple words exist
 * - Takes first two letters if single word
 * - Handles edge cases like "FCV-Venlo JO10-4"
 */
export function getTeamInitials(teamName: string): string {
  if (!teamName) return '?';

  // Clean up and split by spaces, hyphens can be part of words
  const cleaned = teamName.trim();
  if (!cleaned) return '?';

  // Split by spaces to get words
  const words = cleaned.split(/\s+/).filter((w) => w.length > 0);

  if (words.length === 0) return '?';

  if (words.length === 1) {
    // Single word: take first two letters
    return words[0].substring(0, 2).toUpperCase();
  }

  // Multiple words: take first letter of first two words
  return (words[0][0] + words[1][0]).toUpperCase();
}

/**
 * Parse a time string (HH:mm) into hours and minutes
 */
export function parseTime(timeString: string): { hours: number; minutes: number } | null {
  if (!timeString) return null;

  const match = /^(\d{1,2}):(\d{2})/.exec(timeString);
  if (!match) return null;

  return {
    hours: Number.parseInt(match[1], 10),
    minutes: Number.parseInt(match[2], 10),
  };
}

/**
 * Filter out matches that have already been played
 * Used for narrow mode carousel display
 */
export function filterTodayRemaining(matches: Match[], referenceDate: Date = new Date()): Match[] {
  if (!matches || matches.length === 0) {
    return [];
  }

  // Get today's date key
  const todayYear = referenceDate.getFullYear();
  const todayMonth = String(referenceDate.getMonth() + 1).padStart(2, '0');
  const todayDay = String(referenceDate.getDate()).padStart(2, '0');
  const todayKey = `${todayYear}-${todayMonth}-${todayDay}`;

  const currentHours = referenceDate.getHours();
  const currentMinutes = referenceDate.getMinutes();

  return matches.filter((match) => {
    const matchDateKey = extractDateKey(match.wedstrijddatum);

    // Future days: exclude (narrow mode only shows today)
    if (matchDateKey > todayKey) {
      return false;
    }

    // Past days: exclude
    if (matchDateKey < todayKey) {
      return false;
    }

    // Today: check if match time is in the future
    const matchTime = parseTime(match.aanvangstijd);
    if (!matchTime) {
      // If no valid time, include the match (benefit of the doubt)
      return true;
    }

    if (matchTime.hours > currentHours) {
      return true;
    }
    if (matchTime.hours === currentHours && matchTime.minutes >= currentMinutes) {
      return true;
    }

    return false;
  });
}

/**
 * Paginate an array of matches into slides of a given size
 */
export function paginateMatches(matches: Match[], rowsPerPage: number): Match[][] {
  if (!matches || matches.length === 0 || rowsPerPage <= 0) {
    return [];
  }

  const pages: Match[][] = [];
  for (let i = 0; i < matches.length; i += rowsPerPage) {
    pages.push(matches.slice(i, i + rowsPerPage));
  }

  return pages;
}

/**
 * Filter out past matches from today's group while keeping all matches for future dates
 * Used for narrow mode carousel display across multiple dates
 */
export function filterTodayRemainingFromGroups(
  groups: MatchGroup[],
  referenceDate: Date = new Date()
): MatchGroup[] {
  if (!groups || groups.length === 0) {
    return [];
  }

  // Get today's date key
  const todayYear = referenceDate.getFullYear();
  const todayMonth = String(referenceDate.getMonth() + 1).padStart(2, '0');
  const todayDay = String(referenceDate.getDate()).padStart(2, '0');
  const todayKey = `${todayYear}-${todayMonth}-${todayDay}`;

  const currentHours = referenceDate.getHours();
  const currentMinutes = referenceDate.getMinutes();

  return groups
    .map((group) => {
      // Future dates: keep all matches
      if (group.date > todayKey) {
        return group;
      }

      // Past dates: exclude entirely
      if (group.date < todayKey) {
        return null;
      }

      // Today: filter out matches that have already started
      const filteredMatches = group.matches.filter((match) => {
        const matchTime = parseTime(match.aanvangstijd);
        if (!matchTime) {
          // If no valid time, include the match (benefit of the doubt)
          return true;
        }

        if (matchTime.hours > currentHours) {
          return true;
        }
        if (matchTime.hours === currentHours && matchTime.minutes >= currentMinutes) {
          return true;
        }

        return false;
      });

      // If no matches remain for today, skip this group
      if (filteredMatches.length === 0) {
        return null;
      }

      return {
        ...group,
        matches: filteredMatches,
      };
    })
    .filter((group): group is MatchGroup => group !== null);
}

export interface CarouselSlide {
  dateLabel: string;
  matches: Match[];
}

/**
 * Build carousel slides from match groups, paginating each date
 * Each slide contains a date label and matches for that page
 */
export function buildCarouselSlides(groups: MatchGroup[], rowsPerPage: number): CarouselSlide[] {
  if (!groups || groups.length === 0 || rowsPerPage <= 0) {
    return [];
  }

  const slides: CarouselSlide[] = [];

  for (const group of groups) {
    const pages = paginateMatches(group.matches, rowsPerPage);

    for (const pageMatches of pages) {
      slides.push({
        dateLabel: group.dateLabel,
        matches: pageMatches,
      });
    }
  }

  return slides;
}

/**
 * Fetch matches from the Sportlink API
 */
export async function fetchMatches(clientId: string, daysAhead: number): Promise<Match[]> {
  const url = buildApiUrl(clientId, daysAhead);
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  return response.json();
}
